import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import BrowserInteractionTime from 'browser-interaction-time';
import { validateDwellBaseEvent } from './eventValidation';
const DEFAULT_TIMING = {
  initialPollInterval: 5000,
  idleTimeout: 30000,
  multiplierCallback: x => x + x
};

/**
 * Helper class to record the amount of time a user has spent on a page.
 *
 * Two kinds of dwell events are fired. Dwell milestone events are fired periodically while the
 * user is on the page, at increasing intervals (by default, 5s, 10s, 20s, 40s, etc.). A final
 * dwell event will also be fired when the user leaves the page.
 *
 * Note that the event data passed to this helper should be compatible with a 'track' event.
 * See https://extranet.atlassian.com/display/PData/Track+Events for more information.
 *
 * Example usage:
 *
 * const baseEventData = {
 *   actionSubject: 'page',
 *   actionSubjectId: 'aa4fd6c5-28f4-40fa-807a-765a39957cb5',
 *   source: 'page',
 *   containerType: 'space',
 *   containerId: '340f1acf-d15b-4677-88c6-4671d23fb131',
 * }
 *
 * const helper = new DwellTimeHelper(sendTrackEventFunction, baseEventData, searchSessionId)
 * helper.start();
 */
export default class DwellTimeHelper {
  /**
   * Callback for sending a 'track' analytics event. This callback should either be the
   * 'sendTrackEvent' method on the AnalyticsWebClient, or a method that eventually delegates to it.
   * The data for the dwell event will be passed to this method for sending.
   *
   * @callback trackCallback
   * @param {Object} eventData - the base event data that will be added to the track event.
   */

  /**
   * Creates and initialises the DwellTimeHelper.
   *
   * @param {trackCallback} trackCallback - the track callback responsible for actually sending
   * the event.
   * @param {Object} eventData - the base data that will be added to the track event.
   * Additional dwell time attributes will be appended to this data. Note that you don't need to
   * include the 'action' property, as this will be added automatically.
   * @param {?string} searchSessionId - a unique identifier for the related search session.
   * @param {?Object} timingOptions - for the purpose of debugging, you can override the default
   * timing used for measuring dwell time. The following values are possible:
   * - initialPollInterval: how long to wait before the first dwell milestone event
   * is fired (in seconds). Default = 5.
   * - pollBackoffRate: how much to multiply the last poll interval by on each dwell milestone.
   * event. Default = 2.
   * - idleTimeout: how long to wait for no user activity before pausing
   * the dwell timer (in seconds). Default = 30s.
   */
  constructor(trackCallback, _eventData, _searchSessionId, timingOptions) {
    /**
     * Starts the dwell timer. This method should be called upon the initial page load.
     */
    _defineProperty(this, "start", () => {
      this.browserInteractionTime.startTimer();
      this.browserInteractionTime.addTimeIntervalEllapsedCallback({
        callback: timeInMilliseconds => this._sendDwellEvent(this._eventData, this._searchSessionId, timeInMilliseconds, false),
        timeInMilliseconds: this._timing.initialPollInterval,
        multiplier: this._timing.multiplierCallback
      });
      if (typeof window !== 'undefined') {
        window.addEventListener('beforeunload', this._onBeforeUnload);
      }
    });
    /**
     * Stops the dwell timer and sends the final dwell event. Call this method if you wish to
     * finalise a dwell measurement without starting a new one. When switching to a new page in a
     * single-page app, use newPage instead.
     */
    _defineProperty(this, "stop", () => {
      if (this.browserInteractionTime.isRunning()) {
        this._sendDwellEvent(this._eventData, this._searchSessionId, this.browserInteractionTime.getTimeInMilliseconds(), true);
      }
      this.browserInteractionTime.stopTimer();
    });
    /**
     * Send the final dwell event and reinitialise the dwell timer for a new page.
     * This method should be called whenever a new page is switched to in a single-page app context.
     *
     * @param {Object} eventData - the base data that will be added to the track event.
     * @param {?string} searchSessionId - the new search session ID.
     */
    _defineProperty(this, "newPage", (eventData, searchSessionId) => {
      validateDwellBaseEvent(eventData);
      this.stop();
      this.browserInteractionTime.reset();
      this.setEventData(eventData);
      this.setSearchSessionId(searchSessionId);
      this.start();
    });
    _defineProperty(this, "setEventData", eventData => {
      this._eventData = eventData;
    });
    _defineProperty(this, "getEventData", () => this._eventData);
    _defineProperty(this, "setSearchSessionId", searchSessionId => {
      this._searchSessionId = searchSessionId;
    });
    _defineProperty(this, "getSearchSessionId", () => this._searchSessionId);
    _defineProperty(this, "destroy", () => {
      this.browserInteractionTime.destroy();
      if (typeof window !== 'undefined') {
        window.removeEventListener('beforeunload', this._onBeforeUnload);
      }
    });
    _defineProperty(this, "_onBeforeUnload", () => {
      this.stop();
      this.destroy();
    });
    _defineProperty(this, "_sendDwellEvent", (eventData, searchSessionId, timeInMilliseconds, isFinalDwellEvent) => {
      const dwellAttributes = {
        dwellTime: timeInMilliseconds,
        searchReferrer: searchSessionId,
        finalDwellEvent: isFinalDwellEvent
      };
      this._trackCallback({
        action: 'dwelled',
        actionSubject: eventData.actionSubject,
        actionSubjectId: eventData.actionSubjectId,
        source: eventData.source,
        containerType: eventData.containerType,
        containerId: eventData.containerId,
        objectType: eventData.objectType,
        objectId: eventData.objectId,
        attributes: _objectSpread(_objectSpread({}, dwellAttributes), eventData.attributes),
        tags: eventData.tags
      });
    });
    if (!trackCallback) {
      throw new Error('trackCallback is required for DwellTimeHelper');
    }
    validateDwellBaseEvent(_eventData);
    this._trackCallback = trackCallback;
    this._eventData = _eventData;
    this._searchSessionId = _searchSessionId;
    this._timing = _objectSpread(_objectSpread({}, DEFAULT_TIMING), timingOptions);
    this.browserInteractionTime = new BrowserInteractionTime({
      idleTimeoutMs: this._timing.idleTimeout,
      checkCallbacksIntervalMs: 500
    });
  }
}